import React, { useState, useEffect } from 'react';
import { adminApi } from '../api';
import type { AdminUser, WalletDetail, WalletPointLog } from '../types';
import UserWalletChart from './UserWalletChart';
import { STATUS } from '../../styles/tokens';

const REF_TYPE_LABEL: Record<string, string> = {
  'GenerationRecord': 'AI 生成',
  'admin-grant':      '管理員補點',
  'initial':          '免費額度',
  'standalone':       'AI 生成',
};

// 每點對應的售價（TWD）— 對應後端 pricing.ts 的 TWD_PER_POINT 常數
const TWD_PER_POINT = 0.1;

/**
 * 計算單筆扣點 log 的毛利（TWD）。
 * 售價 = |delta| × TWD_PER_POINT；毛利 = 售價 − costTwd。
 * 僅在 delta < 0（扣點）且有 costTwd 時回傳；其他情況回傳 null。
 */
const computeGrossProfit = (log: WalletPointLog): number | null => {
  if (log.delta >= 0 || log.costTwd == null) return null;
  const revenue = Math.abs(log.delta) * TWD_PER_POINT;
  return revenue - log.costTwd;
};

/** 毛利率 = 毛利 ÷ 售價 */
const computeGrossMargin = (log: WalletPointLog): number | null => {
  const gp = computeGrossProfit(log);
  if (gp == null) return null;
  const revenue = Math.abs(log.delta) * TWD_PER_POINT;
  if (revenue === 0) return null;
  return gp / revenue;
};

const sortByMonthDesc = (a: WalletDetail, b: WalletDetail) =>
  b.year !== a.year ? b.year - a.year : b.month - a.month;

// 計算累計扣除點數（pointLogs 中 delta < 0 的部分加總）
const sumDeducted = (logs: WalletPointLog[]): number =>
  logs.reduce((sum, log) => sum + (log.delta < 0 ? -log.delta : 0), 0);

// 計算累計原始成本（TWD）：僅統計有 costTwd 的 log
const sumCostTwd = (logs: WalletPointLog[]): number =>
  logs.reduce((sum, log) => sum + (log.costTwd ?? 0), 0);

// ─────────────────────────────────────────────────────────
// 補點表單
// ─────────────────────────────────────────────────────────
const GrantPointsForm: React.FC<{
  accountId: string;
  onGranted: () => void;
}> = ({ accountId, onGranted }) => {
  const [points, setPoints] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const pts = parseInt(points, 10);
    if (!pts || pts <= 0) { setError('請輸入正整數'); return; }
    setSubmitting(true);
    setError('');
    try {
      await adminApi.grantUserPoints(accountId, pts, reason || undefined);
      setPoints('');
      setReason('');
      onGranted();
    } catch (err: any) {
      setError(err.message || '補點失敗');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={`${STATUS.info} rounded-lg p-4`}>
      <h4 className="text-sm font-medium text-blue-800 dark:text-blue-300 mb-3">補充點數</h4>
      <form onSubmit={handleSubmit} className="flex flex-wrap gap-2 items-end">
        <div>
          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">點數</label>
          <input
            type="number"
            min="1"
            value={points}
            onChange={e => setPoints(e.target.value)}
            placeholder="例如：500"
            className="border dark:border-gray-600 rounded px-3 py-1.5 text-sm w-28 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          />
        </div>
        <div className="flex-1 min-w-[120px]">
          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">原因（選填）</label>
          <input
            type="text"
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="例如：活動補贈"
            className="w-full border dark:border-gray-600 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          />
        </div>
        <button
          type="submit"
          disabled={submitting}
          className="bg-blue-600 text-white px-4 py-1.5 rounded text-sm hover:bg-blue-700 disabled:opacity-50 whitespace-nowrap"
        >
          {submitting ? '補點中...' : '確認補點'}
        </button>
      </form>
      {error && <p className="text-red-500 text-xs mt-2">{error}</p>}
    </div>
  );
};

// ─────────────────────────────────────────────────────────
// 單一錢包的點數變動明細
// ─────────────────────────────────────────────────────────
const PointLogTable: React.FC<{ logs: WalletPointLog[] }> = ({ logs }) => {
  const [showHidden, setShowHidden] = useState(false);

  if (logs.length === 0) {
    return <p className="text-xs text-gray-400">無點數變動記錄</p>;
  }
  return (
    <div className="space-y-2">
      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => setShowHidden(prev => !prev)}
          className="text-xs text-blue-500 dark:text-blue-400 hover:underline"
        >
          {showHidden ? '隱藏進階欄位' : '顯示隱藏欄位'}
        </button>
      </div>
      <table className="w-full text-xs">
        <thead>
          <tr className="text-gray-400 dark:text-gray-500">
            <th className="py-1 text-left pr-4 whitespace-nowrap">時間</th>
            <th className="py-1 text-left pr-4 whitespace-nowrap">類型</th>
            <th className="py-1 text-left pr-4 whitespace-nowrap">說明</th>
            <th className="py-1 text-right pr-4 whitespace-nowrap">成本 (TWD)</th>
            <th className="py-1 text-right pr-4 whitespace-nowrap">變動</th>
            <th className={`py-1 text-right whitespace-nowrap ${showHidden ? 'pr-4' : ''}`}>毛利率</th>
            {showHidden && (
              <>
                <th className="py-1 text-right pr-4 whitespace-nowrap">毛利 (TWD)</th>
                <th className="py-1 text-right whitespace-nowrap">匯率</th>
                <th className="py-1 text-right whitespace-nowrap">利益率</th>
              </>
            )}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
          {[...logs].reverse().map(log => {
            const grossProfit = computeGrossProfit(log);
            const grossMargin = computeGrossMargin(log);
            return (
              <tr key={log._id}>
                <td className="py-1.5 pr-4 text-gray-500 dark:text-gray-400 whitespace-nowrap">
                  {new Date(log.createdAt).toLocaleString('zh-TW')}
                </td>
                <td className="py-1.5 pr-4 whitespace-nowrap">
                  <span className={`px-1.5 py-0.5 rounded text-xs whitespace-nowrap ${
                    log.delta > 0
                      ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300'
                      : 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300'
                  }`}>
                    {log.refType ? (REF_TYPE_LABEL[log.refType] ?? log.refType) : '系統'}
                  </span>
                </td>
                <td className="py-1.5 pr-4 text-gray-600 dark:text-gray-400">{log.reason}</td>
                <td className="py-1.5 pr-4 text-right text-gray-500 dark:text-gray-400 whitespace-nowrap">
                  {log.costTwd != null ? log.costTwd.toFixed(4) : '—'}
                </td>
                <td className={`py-1.5 pr-4 text-right font-medium ${
                  log.delta > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'
                }`}>
                  {log.delta > 0 ? `+${log.delta}` : log.delta}
                </td>
                <td className={`py-1.5 text-right whitespace-nowrap ${showHidden ? 'pr-4' : ''} ${
                  grossMargin == null
                    ? 'text-gray-500 dark:text-gray-400'
                    : grossMargin >= 0
                      ? 'text-emerald-600 dark:text-emerald-400'
                      : 'text-red-500 dark:text-red-400'
                }`}>
                  {grossMargin != null ? `${(grossMargin * 100).toFixed(1)}%` : '—'}
                </td>
                {showHidden && (
                  <>
                    <td className={`py-1.5 pr-4 text-right whitespace-nowrap ${
                      grossProfit == null
                        ? 'text-gray-500 dark:text-gray-400'
                        : grossProfit >= 0
                          ? 'text-emerald-600 dark:text-emerald-400'
                          : 'text-red-500 dark:text-red-400'
                    }`}>
                      {grossProfit != null ? grossProfit.toFixed(4) : '—'}
                    </td>
                    <td className="py-1.5 text-right text-gray-500 dark:text-gray-400 whitespace-nowrap">
                      {log.usdToTwd != null ? log.usdToTwd : '—'}
                    </td>
                    <td className="py-1.5 text-right text-gray-500 dark:text-gray-400 whitespace-nowrap">
                      {log.profitRate != null ? `${(log.profitRate * 100).toFixed(1)}%` : '—'}
                    </td>
                  </>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// ─────────────────────────────────────────────────────────
// 月份錢包列表（可展開查看明細）
// ─────────────────────────────────────────────────────────
const WalletMonthsTable: React.FC<{
  wallets: WalletDetail[];
}> = ({ wallets }) => {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const toggleExpand = (id: string) =>
    setExpandedId(prev => (prev === id ? null : id));

  return (
    <div className="border border-gray-100 dark:border-gray-700 rounded-lg overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 dark:bg-gray-900 text-xs text-gray-500 dark:text-gray-400">
          <tr>
            <th className="px-4 py-2 text-left whitespace-nowrap">月份</th>
            <th className="px-4 py-2 text-right whitespace-nowrap">總量</th>
            <th className="px-4 py-2 text-right whitespace-nowrap">已用</th>
            <th className="px-4 py-2 text-right whitespace-nowrap">剩餘</th>
            <th className="px-4 py-2 text-right whitespace-nowrap">次數</th>
            <th className="px-4 py-2 text-right whitespace-nowrap">成本 (TWD)</th>
            <th className="px-4 py-2 text-left"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
          {wallets.map(w => {
            const isExpanded = expandedId === w._id;
            const used = sumDeducted(w.pointLogs);
            const totalGranted = w.pointLogs.reduce((sum, log) => sum + (log.delta > 0 ? log.delta : 0), 0);
            // 扣點次數：pointLogs 中 delta < 0 的筆數
            const deductCount = w.pointLogs.reduce((count, log) => count + (log.delta < 0 ? 1 : 0), 0);
            const totalCostTwd = sumCostTwd(w.pointLogs);
            return (
              <React.Fragment key={w._id}>
                <tr
                  className="hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer"
                  onClick={() => toggleExpand(w._id)}
                >
                  <td className="px-4 py-2.5 font-medium text-gray-800 dark:text-gray-200">
                    {w.year} 年 {w.month} 月
                  </td>
                  <td className="px-4 py-2.5 text-right text-blue-600 dark:text-blue-400">{totalGranted}</td>
                  <td className="px-4 py-2.5 text-right text-orange-600 dark:text-orange-400">{used}</td>
                  <td className="px-4 py-2.5 text-right">
                    <span className={`font-medium ${
                      w.currentPoints > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'
                    }`}>
                      {w.currentPoints}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-right text-gray-600 dark:text-gray-400">{deductCount}</td>
                  <td className="px-4 py-2.5 text-right text-gray-600 dark:text-gray-400">
                    {totalCostTwd > 0 ? totalCostTwd.toFixed(4) : '—'}
                  </td>
                  <td className="px-4 py-2.5 text-right text-xs text-blue-500 dark:text-blue-400">
                    {isExpanded ? '收合 ▲' : '明細 ▼'}
                  </td>
                </tr>
                {isExpanded && (
                  <tr>
                    <td colSpan={7} className="bg-gray-50 dark:bg-gray-900/50 px-4 py-3">
                      <PointLogTable logs={w.pointLogs} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// ─────────────────────────────────────────────────────────
// 主 modal
// ─────────────────────────────────────────────────────────
const UserWalletModal: React.FC<{
  user: AdminUser;
  onClose: () => void;
}> = ({ user, onClose }) => {
  const [wallets, setWallets] = useState<WalletDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<'list' | 'chart'>('list');
  // 「現在」的時間戳：在切換到圖表 view 的 click handler 中以 Date.now() 寫入
  const [chartNowMs, setChartNowMs] = useState(0);

  const loadWallets = () => {
    adminApi.getUserWallets(user._id)
      .then(data => setWallets(data.sort(sortByMonthDesc)))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(loadWallets, [user._id]);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg w-full max-w-5xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-700 shrink-0">
          <h3 className="font-semibold text-gray-800 dark:text-gray-100">
            點數記錄 — <span className="text-blue-600 dark:text-blue-400">{user.username}</span>
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-lg leading-none"
          >
            ✕
          </button>
        </div>

        <div className="overflow-y-auto flex-1 px-6 py-4 space-y-5">
          <div className="text-xs text-gray-500 dark:text-gray-400">
            免費額度：
            {user.freeQuota == null
              ? <span className="italic text-gray-400 dark:text-gray-500">使用系統預設值</span>
              : <span className="font-medium text-gray-700 dark:text-gray-300">{user.freeQuota} 點 / 月</span>}
            <span className="ml-1 text-gray-400 dark:text-gray-500">（每月錢包惰性建立時採用）</span>
          </div>

          <GrantPointsForm accountId={user._id} onGranted={loadWallets} />

          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-medium text-gray-600 dark:text-gray-400">月份記錄</h4>
              <div className="inline-flex rounded-md overflow-hidden border border-gray-200 dark:border-gray-700 text-xs">
                <button
                  type="button"
                  onClick={() => setView('list')}
                  className={`px-3 py-1 ${
                    view === 'list'
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  列表
                </button>
                <button
                  type="button"
                  onClick={() => { setChartNowMs(Date.now()); setView('chart'); }}
                  className={`px-3 py-1 border-l border-gray-200 dark:border-gray-700 ${
                    view === 'chart'
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  圖表
                </button>
              </div>
            </div>
            {loading
              ? <p className="text-sm text-gray-400">載入中...</p>
              : wallets.length === 0
                ? <p className="text-sm text-gray-400">尚無點數記錄</p>
                : view === 'list'
                  ? <WalletMonthsTable wallets={wallets} />
                  : <UserWalletChart wallets={wallets} nowMs={chartNowMs} />
            }
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserWalletModal;
